/**
 * useStartInspection — supervisor-side mutation that calls the
 * start-inspection Edge Function. Opens a new inspection pass on a
 * cleaning task. Surfaces structured error codes from the Edge
 * Function + RPC as AppError.code so the inline blocker UI can
 * render them via the standard pattern (CLAUDE.md ERROR 4).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export interface StartInspectionVars {
  cleaningTaskId: string
}

export interface StartInspectionResult {
  inspection_id:    string
  cleaning_task_id: string
  room_id:          string
  pass_number:      number
  status:           'in_progress'
}

export function useStartInspection(): UseMutationResult<StartInspectionResult, AppError, StartInspectionVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<StartInspectionResult, AppError, StartInspectionVars>({
    mutationFn: async (vars): Promise<StartInspectionResult> => {
      const { data, error } = await supabase.functions.invoke<StartInspectionResult>('start-inspection', {
        body:    { cleaning_task_id: vars.cleaningTaskId },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'REQUEST_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Start-inspection returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['inspections'] })
      void queryClient.invalidateQueries({ queryKey: ['inspections', vars.cleaningTaskId] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'FORBIDDEN_ROLE':                  return 'Only supervisors can start an inspection.'
    case 'FORBIDDEN_ORG':                   return 'This task does not belong to your organization.'
    case 'TASK_NOT_FOUND':                  return 'This task could not be found.'
    case 'INSPECTION_ALREADY_IN_PROGRESS':  return 'An inspection is already in progress on this task.'
    case 'PASS_LIMIT_EXCEEDED':             return 'This task has hit the maximum number of inspection passes (20).'
    case 'RATE_LIMITED':                    return 'Too many start requests in a row. Try again in a moment.'
    default:                                return detail || 'Could not start the inspection.'
  }
}
